import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Pencil, Mail, Phone, MapPin, Calendar, Users, Church, Hash, UserCircle, Crown } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";

type Member = Tables<"members">;

interface Props {
  open: boolean;
  onClose: () => void;
  member: Member | null;
  onEdit: (member: Member) => void;
}

/** Generate a short UID from the member's UUID */
function getMemberUID(id: string): string {
  return `M-${id.slice(0, 6).toUpperCase()}`;
}

export default function MemberDetailDialog({ open, onClose, member, onEdit }: Props) {
  const { data: familyLinks = [] } = useQuery({
    queryKey: ["member_families", member?.id],
    enabled: !!member,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("family_members")
        .select("id, relationship, family_id")
        .eq("member_id", member!.id);
      if (error) throw error;
      return data;
    },
  });

  const familyIds = familyLinks.map((fl) => fl.family_id);

  const { data: families = [] } = useQuery({
    queryKey: ["families_detail", familyIds],
    enabled: familyIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("families")
        .select("id, family_name")
        .in("id", familyIds);
      if (error) throw error;
      return data;
    },
  });

  const { data: familyMembers = [] } = useQuery({
    queryKey: ["family_members_detail", familyIds],
    enabled: familyIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("family_members")
        .select("id, relationship, family_id, member_id")
        .in("family_id", familyIds);
      if (error) throw error;
      return data;
    },
  });

  const otherMemberIds = familyMembers
    .filter((fm) => fm.member_id !== member?.id)
    .map((fm) => fm.member_id);

  const { data: otherMembers = [] } = useQuery({
    queryKey: ["other_family_members", otherMemberIds],
    enabled: otherMemberIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("id, full_name, phone, email, profile_photo_url")
        .in("id", otherMemberIds);
      if (error) throw error;
      return data;
    },
  });

  // Fetch leadership roles
  const { data: leadershipRoles = [] } = useQuery({
    queryKey: ["member_leadership", member?.id],
    enabled: !!member,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organization_leaders")
        .select("organization_id, title_id")
        .eq("member_id", member!.id);
      if (error) throw error;
      if (!data || data.length === 0) return [];
      const orgIds = data.map((d: any) => d.organization_id);
      const { data: orgs, error: orgErr } = await supabase
        .from("organizations")
        .select("id, name, type, level")
        .in("id", orgIds);
      if (orgErr) throw orgErr;

      // Fetch titles
      const titleIds = [...new Set(data.filter((d: any) => d.title_id).map((d: any) => d.title_id))];
      let titleMap = new Map<string, string>();
      if (titleIds.length > 0) {
        const { data: titles } = await supabase
          .from("organization_titles")
          .select("id, name")
          .in("id", titleIds);
        titleMap = new Map((titles || []).map((t: any) => [t.id, t.name]));
      }

      const titleByOrg = new Map(data.map((d: any) => [d.organization_id, d.title_id ? titleMap.get(d.title_id) || null : null]));
      return (orgs || []).map((org: any) => ({
        ...org,
        title_name: titleByOrg.get(org.id) || null,
      }));
    },
  });

  if (!member) return null;

  const m = member as any;
  const displayName = m.first_name
    ? [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ")
    : member.full_name;

  const initials = displayName
    .split(" ")
    .map((n: string) => n[0])
    .join("");

  const fullAddress = [member.address, m.city, m.state, m.zip_code].filter(Boolean).join(", ");

  const DetailRow = ({ icon: Icon, label, value }: { icon: any; label: string; value: string | null | undefined }) => {
    if (!value) return null;
    return (
      <div className="flex items-start gap-3">
        <Icon className="w-4 h-4 mt-0.5 text-muted-foreground" />
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-sm text-foreground">{value}</p>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto [&>button]:hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="font-heading">Member Details</DialogTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => { onClose(); onEdit(member); }}>
                <Pencil className="w-4 h-4 mr-2" /> Edit
              </Button>
              <Button variant="outline" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16">
            <AvatarFallback className="bg-primary/10 text-primary text-lg font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{displayName}</h3>
            <p className="text-xs text-muted-foreground font-mono mt-0.5">{getMemberUID(member.id)}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge
                variant={member.status === "Active" ? "default" : "secondary"}
                className={member.status === "Active" ? "bg-success/10 text-success border-success/20" : ""}
              >
                {member.status}
              </Badge>
              {m.gender && <Badge variant="outline">{m.gender}</Badge>}
              {member.department && <Badge variant="secondary">{member.department}</Badge>}
              {member.baptism_status && <Badge variant="outline">Baptized</Badge>}
            </div>
          </div>
        </div>

        <Separator />

        <div className="space-y-3">
          <h4 className="text-sm font-medium text-foreground">Contact Information</h4>
          <DetailRow icon={Mail} label="Email" value={member.email} />
          <DetailRow icon={Phone} label="Phone" value={member.phone} />
          <DetailRow icon={MapPin} label="Address" value={fullAddress || null} />
          <DetailRow icon={UserCircle} label="Gender" value={m.gender} />
          <DetailRow icon={Calendar} label="Date of Birth" value={m.date_of_birth} />
          <DetailRow icon={Calendar} label="Date Joined" value={member.date_joined} />
          <DetailRow icon={Church} label="Family / Household" value={member.family_household} />
        </div>

        {leadershipRoles.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-500" /> Leadership Roles
              </h4>
              <div className="flex flex-wrap gap-2">
                {leadershipRoles.map((org: any) => (
                  <Badge key={org.id} variant="outline" className="text-xs capitalize">
                    {org.title_name ? `${org.title_name} — ` : ""}{org.name} ({org.type})
                  </Badge>
                ))}
              </div>
            </div>
          </>
        )}

        {(m.education_status) && (
          <>
            <Separator />
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground">Education Information</h4>
              <p className="text-sm text-muted-foreground">Status: {m.education_status}</p>
              {m.school_name && <p className="text-sm text-muted-foreground">School: {m.school_name}</p>}
              {m.year_level && <p className="text-sm text-muted-foreground">Year/Level: {m.year_level}</p>}
              {m.field_of_study && <p className="text-sm text-muted-foreground">Field of Study: {m.field_of_study}</p>}
            </div>
          </>
        )}

        {(m.employment_status) && (
          <>
            <Separator />
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground">Employment Information</h4>
              <p className="text-sm text-muted-foreground">Status: {m.employment_status}</p>
              {m.occupation && <p className="text-sm text-muted-foreground">Occupation: {m.occupation}</p>}
              {m.industry && <p className="text-sm text-muted-foreground">Industry: {m.industry}</p>}
              {m.employer_name && <p className="text-sm text-muted-foreground">Employer: {m.employer_name}</p>}
            </div>
          </>
        )}

        {member.notes && (
          <>
            <Separator />
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground">Notes</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{member.notes}</p>
            </div>
          </>
        )}

        {families.length > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                <Users className="w-4 h-4" /> Family Members
              </h4>
              {families.map((family) => {
                const membersInFamily = familyMembers
                  .filter((fm) => fm.family_id === family.id && fm.member_id !== member.id)
                  .map((fm) => ({ ...fm, member: otherMembers.find((om) => om.id === fm.member_id) }))
                  .filter((fm) => fm.member);

                return (
                  <div key={family.id} className="rounded-lg border border-border p-3 space-y-2">
                    <p className="text-sm font-medium text-foreground">{family.family_name}</p>
                    {membersInFamily.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No other family members</p>
                    ) : (
                      <div className="space-y-2">
                        {membersInFamily.map((fm) => (
                          <div key={fm.id} className="flex items-center gap-3">
                            <Avatar className="w-8 h-8">
                              <AvatarFallback className="bg-secondary text-secondary-foreground text-xs">
                                {fm.member!.full_name.split(" ").map((n) => n[0]).join("")}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm text-foreground">{fm.member!.full_name}</p>
                              {fm.relationship && <p className="text-xs text-muted-foreground">{fm.relationship}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
